import {
  useState,
  useEffect,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  DateTime,
} from 'luxon';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  decodeParam,
} from 'web-utils';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  Spinner,
  Calendar,
} from 'web-components';
import {
  useCompleteTutorial,
} from 'web-hooks'

import {
  Flex,
} from 'web-styled';
const mapState = (state) => {
  const {
    openedTooltips,
    newWorkoutEventModal,
    calendarSelectedUser,
  } = getUiState([
    'openedTooltips',
    'newWorkoutEventModal',
    'activityLoggerModal',
    'scheduleWorkoutModal',
    'calendarSelectedUser',
    'newWorkoutsEventUserSelected',
  ])(state);

  const profile = coreSelectors.getProfile(state);
  const events = coreSelectors.getEventsForBigCalendar(calendarSelectedUser)(state);

  return {
    events,
    clients: coreSelectors.getCalendarClients(state),
    profileId: profile.id,
    readStatus: getStatus(
      state,
      [
        'workoutEvents.requests.fetchWorkoutEvents.status',
        'activityLogs.requests.fetchActivityLogs.status',
        'personalWorkouts.requests.fetchPersonalWorkouts.status',
      ],
      true,
    ),
    tutorialSteps: profile.user_settings.tutorial_steps,
    openedTooltips,
    newWorkoutEventModal,
    calendarSelectedUser,
  };
};

const CalendarPage = () => {
  const params = useParams();
  const { completeTutorialStep } = useCompleteTutorial()

  const currentDate = (params.date && DateTime.fromISO(params.date).isValid)
    ? DateTime.fromISO(params.date)
    : DateTime.local();
  const [currentRange, setCurrentRange] = useState({
    from: currentDate.startOf('month'),
    to: currentDate.endOf('month'),
  });

  const {
    events,
    clients,
    profileId,
    readStatus,
    tutorialSteps,
    openedTooltips,
    newWorkoutEventModal,
    calendarSelectedUser,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => () => {
    // clear tooltips after leave pages
    if (openedTooltips.some(el => el.key === 'review_calendar')) {
      dispatch(
        coreActions.requestMarkTutotialStepsComplite({
          steps: ['review_calendar'],
        }),
      );
      completeTutorialStep('review_calendar')

      dispatch(
        uiActions.setUiState({
          openedTooltips: [],
        }),
      );
    }
  }, [openedTooltips, tutorialSteps])

  const fetchEvents = ({ from, to, userId }: any) => {
    const qs = {
      range_from: from,
      range_to: to,
      scope: userId ? 'all' : 'all_with_clients',
    };

    dispatch(
      coreActions.requestLoadWorkoutEvents(userId
        ? {
          params: qs,
          userId,
        }
        : {
          params: qs,
        }),
    );

    dispatch(
      coreActions.requestFetchClientEntity({
        clientId: userId,
        params: qs,
        list: userId ? String(userId) : 'all',
        resourceType: 'activityLogs',
      }),
    );
  };

  const fetchWorkouts = (userId: number) => {
    if (userId && userId !== profileId) {
      dispatch(
        coreActions.requestLoadPersonalWorkouts({
          userId,
        }),
      );
    }
  };

  useEffect(() => {
    const { userId } = decodeParam(window.location.search);
    if (userId) {
      dispatch(
        uiActions.setUiState({
          calendarSelectedUser: Number(userId),
          newWorkoutsEventUserSelected: Number(userId),
        }),
      );
    }
    fetchEvents({
      ...currentRange,
      userId,
    });
    dispatch(
      coreActions.requestLoadPersonalWorkouts({
        userId: userId || profileId,
      }),
    );
  }, []);

  return (
    <Flex
      fillSpace
      onClick={() => {
        if (openedTooltips.some(el => el.key === 'review_calendar')) {
          dispatch(
            uiActions.setUiState({
              openedTooltips: [],
            }),
          );
          completeTutorialStep('review_calendar')
        }
      }}
    >
      <Calendar
        loaded={readStatus.succeeded}
        defaultDate={currentRange.from.toJSDate()}
        onFilter={(userId) => {
          dispatch(
            uiActions.setUiState({
              calendarSelectedUser: userId,
              newWorkoutsEventUserSelected: userId,
            }),
          );
          fetchEvents({
            userId,
            ...currentRange,
          });
          fetchWorkouts(userId);
        }}
        events={events}
        clients={clients}
        isTooltipVisible={openedTooltips.some(el => el.key === 'review_calendar')}
        calendarSelectedUser={calendarSelectedUser}
        onNewWorkoutClick={
          ({ start }) => {
            if (openedTooltips.some(el => el.key === 'schedule_workout_button')) {
              dispatch(
                uiActions.setUiState({
                  openedTooltips: [
                    {
                      key: 'schedule_workout_form'
                    }
                  ],
                }),
              );
            }
            dispatch(
              uiActions.setUiState({
                newWorkoutEventModal: {
                  isOpen: true,
                  initialValues: {
                    client: calendarSelectedUser || profileId,
                    start: DateTime.fromJSDate(start),
                    end: DateTime.fromJSDate(start).plus({ hours: 1 }),
                  },
                },
              }),
            );
          }
        }
        onNavigate={(day) => {
          const from = DateTime.fromJSDate(day).startOf('month');
          const to = DateTime.fromJSDate(day).endOf('month');
          setCurrentRange({
            from,
            to,
          });
          fetchEvents({
            from,
            to,
            userId: calendarSelectedUser,
          });
        }}
      />

      {readStatus.pending && (
        <Spinner over />
      )}
    </Flex>
  );
};

export default CalendarPage;
